import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { WhereIsLombok } from "@/components/WhereIsLombok";
import {
  APPRECIATION,
  GEO_FACTS,
  IMG,
  MILESTONES,
  REGIONS,
  YIELDS,
} from "@/lib/data";

const RECORD_STATS = [
  { k: "×10", l: "Kuta land value multiple since the Mandalika circuit was announced — Rp 50–100K to Rp 500K–1M per m²" },
  { k: "18–22%", l: "land CAGR in the Kuta–Mandalika corridor, 2021–2024" },
  { k: "15–20%", l: "villa price CAGR across South Lombok, 2021–2025" },
];

const OFFICIAL_RECORD = [
  {
    year: "2020",
    event:
      "Pandemic trough — Mandalika-corridor land values fall 10–20%; land inside the SEZ trades near USD 60–80 per m².",
    src: "Lombok 5-Year Market Analysis · lombokinvestment.com",
  },
  {
    year: "2021",
    event:
      "The turn — the Mandalika street circuit opens (Nov 2021). Kuta land that traded at Rp 50,000–100,000/m² before development passes Rp 500,000/m².",
    src: "ITDC · Universitas Airlangga",
  },
  {
    year: "2022",
    event:
      "First MotoGP on the island (March 2022). Kuta villa transactions recover to ~80% of pre-COVID volume; corridor land compounding at 18–22% a year.",
    src: "Lombok Market Analysis · Hub Lombok",
  },
  {
    year: "2023",
    event:
      "Expansion — South Lombok villa prices compound at 15–20% (2021–25 CAGR). National residential index (IHPR) closes the year +1.74% yoy.",
    src: "Bank Indonesia SHPR Q4-2023 · lombokinvestment.com",
  },
  {
    year: "2024",
    event:
      "Peak of the surge — prime South Lombok land appreciates 20–25% in the year; villa-rate momentum in Kuta +38% yoy. National IHPR +1.39% (Q4).",
    src: "Bank Indonesia SHPR · Hub Lombok market data",
  },
  {
    year: "2025",
    event:
      "Normalization — appreciation eases toward 10–16% p.a. as the market matures. KEK Mandalika investment realization reaches Rp 5.74 trillion by mid-2025.",
    src: "Lombok Market Analysis · ITDC · Tribun Lombok",
  },
];

const REFERENCES = [
  {
    n: "1",
    label: "Bank Indonesia — Survei Harga Properti Residensial (SHPR/IHPR), 2023–2025",
    url: "https://www.bi.go.id/id/publikasi/laporan/default.aspx?kategori=survei+harga+properti+residensial+di+pasar+primer&periode=",
  },
  {
    n: "2",
    label: "ITDC — KEK Mandalika development press releases",
    url: "https://www.itdc.co.id/press-release",
  },
  {
    n: "3",
    label: "Universitas Airlangga — Harga Tanah di Mandalika Melambung Tinggi",
    url: "https://unair.ac.id/harga-tanah-di-mandalika-melambung-tinggi-2/",
  },
  {
    n: "4",
    label: "Lombok Investment — 5-Year Real Estate Market Analysis 2020–2025",
    url: "https://lombokinvestment.com/lombok-real-estate-market-5-year-analysis-2020-2025/",
  },
  {
    n: "5",
    label: "Hub Lombok — South Lombok Market Data",
    url: "https://hublombok.com/market-data",
  },
  {
    n: "6",
    label: "Tribun Lombok — Investasi KEK Mandalika Capai Rp 5,74 Triliun hingga Pertengahan 2025",
    url: "https://lombok.tribunnews.com/bisnis/93150/investasi-kek-mandalika-capai-rp574-triliun-hingga-pertengahan-2025",
  },
];

const lineData = APPRECIATION.years.map((y, i) => ({
  year: y,
  "South Lombok": APPRECIATION.lombok[i],
  Bali: APPRECIATION.bali[i],
}));

const ChartTip = ({ active, payload, label, suffix = "" }) =>
  active && payload?.length ? (
    <div className="bg-ink px-4 py-3 font-mono text-[11px] text-cream">
      <p className="text-gold">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="mt-1">
          {p.name}: {p.value}
          {suffix}
        </p>
      ))}
    </div>
  ) : null;

function Hero() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 pt-32 md:px-10 md:pt-44">
      <div className="grid items-center gap-12 md:grid-cols-12">
        <div className="md:col-span-7">
          <Reveal>
            <Eyebrow>The island</Eyebrow>
            <h1 className="mt-5 font-display text-5xl leading-[1] md:text-7xl">
              Lombok, <em className="text-terracotta">plainly.</em>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-moss">
              East of Bali, quieter by far — 4,724 km² of volcanoes, rice
              terraces and a south coast the Indonesian government has chosen
              for its next tourism flagship. Here is what the public record
              says about the island, and what its land has done.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-6">
              {GEO_FACTS.map((f) => (
                <div key={f.k} className="border-t border-pine/15 pt-4">
                  <p className="font-display text-2xl md:text-3xl">{f.k}</p>
                  <p className="mt-1 text-sm text-moss">{f.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="md:col-span-5">
          <div className="relative">
            <span className="absolute -right-3 -top-3 h-full w-full rounded-t-[10rem] border border-gold md:-right-5 md:-top-5" />
            <img
              src={IMG.sandyBeach}
              alt="Selong Belanak beach, Lombok"
              className="relative aspect-[3/4] w-full rounded-t-[10rem] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Milestones() {
  return (
    <section className="grain relative mt-24 bg-ink py-16 text-cream md:mt-36 md:py-20">
      <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-2 gap-x-8 gap-y-10 px-6 md:grid-cols-4 md:px-10">
        {MILESTONES.map((m, i) => (
          <Reveal key={m.year} delay={i * 0.08}>
            <p className="font-mono text-xs tracking-[0.25em] text-gold">{m.year}</p>
            <p className="mt-3 font-display text-lg leading-snug md:text-xl">{m.t}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function MarketData() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-36">
      <Reveal>
        <Eyebrow>Market estimates</Eyebrow>
        <h2 className="mt-5 max-w-2xl font-display text-4xl leading-[1.05] md:text-6xl">
          What the land <em className="text-terracotta">has been doing</em>
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div
            className="rounded-2xl border border-pine/10 bg-white p-6 md:p-8"
            data-testid="chart-appreciation"
          >
            <h3 className="font-display text-xl md:text-2xl">
              Prime land index — South Lombok vs Bali
            </h3>
            <p className="mt-1 text-sm text-sage">Index, 2019 = 100</p>
            <div className="mt-6 h-72 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(26,35,30,0.08)" vertical={false} />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: "#526057", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#859389", fontSize: 11, fontFamily: "JetBrains Mono" }}
                    axisLine={false}
                    tickLine={false}
                    domain={[90, 280]}
                  />
                  <Tooltip content={<ChartTip />} cursor={{ stroke: "rgba(194,109,71,0.4)" }} />
                  <Line
                    type="monotone"
                    dataKey="South Lombok"
                    stroke="#C26D47"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "#C26D47" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Bali"
                    stroke="#859389"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 3, fill: "#859389" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="grain rounded-2xl bg-ink p-6 text-cream md:p-8"
            data-testid="chart-yields"
          >
            <div className="relative z-10">
              <h3 className="font-display text-xl md:text-2xl">
                Gross annual yields by region
              </h3>
              <p className="mt-1 text-sm text-cream/50">Percent per year, 2024 estimates</p>
              <div className="mt-6 h-72 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={YIELDS} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(248,245,240,0.08)" vertical={false} />
                    <XAxis
                      dataKey="region"
                      tick={{ fill: "#859389", fontSize: 10, fontFamily: "JetBrains Mono" }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      angle={-18}
                      dy={8}
                      height={50}
                    />
                    <YAxis
                      tick={{ fill: "#859389", fontSize: 11, fontFamily: "JetBrains Mono" }}
                      axisLine={false}
                      tickLine={false}
                      unit="%"
                    />
                    <Tooltip content={<ChartTip suffix="% p.a." />} cursor={{ fill: "rgba(212,163,89,0.08)" }} />
                    <Bar dataKey="villa" name="Villas" fill="#F5A623" radius={[3, 3, 0, 0]} maxBarSize={22} />
                    <Bar dataKey="land" name="Land" fill="#C26D47" radius={[3, 3, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
        {RECORD_STATS.map((s, i) => (
          <Reveal key={s.k} delay={i * 0.07}>
            <div className="border-t-2 border-terracotta pt-4" data-testid={`record-stat-${i}`}>
              <p className="font-display text-3xl md:text-4xl">{s.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-moss">{s.l}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
          The charts above are indicative market estimates — the record below
          is year by year, as documented by the cited sources.
        </p>
      </Reveal>

      <div className="mt-20" data-testid="official-record">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h3 className="max-w-xl font-display text-3xl leading-tight md:text-5xl">
            The official record, <em className="text-terracotta">2020–2025</em>
          </h3>
          <p className="max-w-sm text-sm leading-relaxed text-moss">
            Land and villa appreciation in Lombok, year by year, as documented
            by the sources below.
          </p>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-pine/10 bg-white">
          {OFFICIAL_RECORD.map((r, i) => (
            <Reveal key={r.year} delay={i * 0.04}>
              <div
                className={`grid gap-2 px-6 py-6 md:grid-cols-12 md:gap-6 md:px-8 ${
                  i > 0 ? "border-t border-pine/10" : ""
                }`}
                data-testid={`record-row-${r.year}`}
              >
                <p className="font-mono text-sm text-terracotta md:col-span-2">{r.year}</p>
                <p className="leading-relaxed text-pine md:col-span-7">{r.event}</p>
                <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-sage md:col-span-3">
                  {r.src}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-pine">
            Sources &amp; references
          </p>
          <ul className="mt-4 grid gap-2 md:grid-cols-2">
            {REFERENCES.map((r) => (
              <li key={r.n} className="text-sm leading-relaxed text-moss">
                <span className="font-mono text-terracotta">{r.n}.</span>{" "}
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-pine/20 underline-offset-4 transition-colors hover:text-terracotta"
                  data-testid={`reference-${r.n}`}
                >
                  {r.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">
            Official figures as published by the cited institutions; market
            analyses are independent estimates. Past performance does not
            guarantee future returns.
          </p>
        </div>
      </div>
    </section>
  );
}

function Regions() {
  return (
    <section className="mx-auto max-w-[1440px] px-6 pb-24 md:px-10 md:pb-36">
      <Reveal>
        <Eyebrow>Where we operate</Eyebrow>
        <h2 className="mt-5 font-display text-4xl leading-[1.05] md:text-6xl">
          Six coasts, <em className="text-terracotta">one island</em>
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-x-6 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
        {REGIONS.map((r, i) => (
          <Reveal key={r.name} delay={(i % 3) * 0.08}>
            <div className="group" data-testid={`region-card-${i}`}>
              <div className="overflow-hidden rounded-t-[7rem]">
                <img
                  src={r.img}
                  alt={r.name}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
                />
              </div>
              <h3 className="mt-5 font-display text-2xl">{r.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-moss">{r.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-16 flex justify-center">
        <Link
          to="/portfolio"
          data-testid="lombok-cta-portfolio"
          className="group inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-cream transition-colors hover:bg-terracotta"
        >
          See what's for sale
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </section>
  );
}

export default function Lombok() {
  return (
    <main>
      <Hero />
      <WhereIsLombok />
      <Milestones />
      <MarketData />
      <Regions />
    </main>
  );
}
